export const caseStudies = [
  {
    id: "culture-in-your-city",
    title: "Culture in Your City",
    context: "WebEthics Solutions · Sept 2025 → Present",
    status: "Live",
    role: "Junior Software Developer",
    hoverImg: "/img/culturecity_hover.png",
    stack: ["Vue.js", "NestJS", "MongoDB", "TypeScript", "Tailwind CSS"],
    description:
      "Production-grade cultural discovery platform connecting users with events, organisations, and communities across Chicago.",
    problem:
      "Build and maintain a living cultural ecosystem — not a listing page, but a platform where users discover, claim, and participate in Chicago's cultural life.",
    approach: [
      "Owned end-to-end delivery of rewards systems, overall performance optimisation, event claiming workflows, podcast integrations, and full administrative tooling.",
      "Independently managed deployment pipelines, performance optimisation, and product evolution — operating like a founding engineer, not a feature implementer.",
      "Leveraged AI-assisted workflows to compress delivery timelines without sacrificing ownership or code quality.",
    ],
    link: "https://cultureinyourcity.com/",
    accent: "#0A84FF",
    accentGlow: "rgba(10,132,255,0.18)",
  },
  {
    id: "sagenest",
    title: "SageNest",
    context: "WebEthics Solutions",
    status: "Shipped",
    role: "Junior Software Developer",
    hoverImg: "/img/sagenest_hover.png",
    stack: ["React.js", "Express.js", "PostgreSQL", "Prisma ORM", "Stripe"],
    description:
      "Booking platform enabling European parents to connect with infant feeding experts and childcare specialists.",
    problem:
      "Design and ship a trust-critical booking platform from zero — schema, payments, deployment, and failure handling included.",
    approach: [
      "Integrated Stripe with full webhook support — handling failed transactions, subscription updates, and edge cases with event-driven reliability.",
      "Managed database schema design and all migrations using Prisma ORM from day one. No technical debt handed off.",
      "Deployed independently on Render with full production ownership — no handholding, no handoff.",
    ],
    link: "https://the-sage-nest.webflow.io/",
    accent: "#34C759",
    accentGlow: "rgba(52,199,89,0.18)",
  },
  {
    id: "deskhero",
    title: "DeskHero",
    context: "WebEthics Solutions",
    status: "Shipped",
    role: "Junior Software Developer",
    hoverImg: "/img/deskhero_hover.png",
    stack: ["React.js", "Component Architecture", "AI Integration"],
    description:
      "AI-powered customer support platform combining structured business knowledge with intelligent ticket routing.",
    problem:
      "Help organisations automate repetitive support workflows while keeping humans in control for high-accountability interactions.",
    approach: [
      "Collaborated with product and AI consultation teams to identify use cases and design automation strategies.",
      "Built query classification and knowledge retrieval systems that reduced repetitive workload while maintaining clear accountability boundaries.",
    ],
    link: "https://www.deskhero.com/",
    accent: "#FF9500",
    accentGlow: "rgba(255,149,0,0.18)",
  },
];

export const showcaseProjects = [
  {
    id: "aethercore",
    title: "AetherCore",
    type: "Gaming AI · Interactive",
    accent: "#7C3AED",
    previewImg: "/img/aethercore_hover.png",
    description:
      "A cinematic gaming AI landing page built to push browser rendering limits — combining Three.js 3D environments, GSAP scroll-driven sequences, and immersive motion design into a single cohesive experience.",
    highlights: [
      "Three.js 3D scenes with custom lighting, camera paths, and animated geometry rendered directly in the browser.",
      "GSAP ScrollTrigger-driven cinematic sequences — each section enters with choreographed animations timed to scroll progress.",
      "Immersive visual identity: WebGL particle effects, glassmorphism overlays, and a dark sci-fi aesthetic.",
      "Built in React with a component-driven architecture, keeping complex animations modular and reusable.",
    ],
    stack: ["React", "Three.js", "GSAP", "Tailwind CSS", "WebGL"],
    liveLink: "https://vishvjeet-rana.github.io/AetherCore-GamingAI-Site/",
    codeLink: "https://github.com/Vishvjeet-Rana/AetherCore-GamingAI-Site",
  },
  {
    id: "iphone",
    title: "3D iPhone Showcase",
    type: "Three.js · WebGL · Product",
    accent: "#C7C7CC",
    previewImg: "/img/iphone_hover.png",
    description:
      "An Apple-inspired iPhone 15 Pro product showcase built to demonstrate mastery of 3D web rendering — featuring a fully interactive 3D model with real-time colour switching and GSAP-driven cinematic transitions.",
    highlights: [
      "Three.js GLTF model loading with custom environment mapping and real-time material overrides for colour switching.",
      "GSAP timeline camera animations — smooth dolly, orbit, and reveal sequences synced to user interaction.",
      "Apple-grade product page aesthetic: precise typography, clean layout, hardware-accurate 3D presentation.",
      "Lightweight React architecture focused on performance — minimal overhead despite a complex 3D scene.",
    ],
    stack: ["React", "Three.js", "GSAP", "WebGL", "GLTF"],
    liveLink: "https://vishvjeet-rana.github.io/iPhone15-Website-Three.js/",
    codeLink: "https://github.com/Vishvjeet-Rana/iPhone15-Website-Three.js",
  },
];

export const experiments = [
  {
    id: "aethercore",
    title: "AetherCore",
    link: "https://vishvjeet-rana.github.io/AetherCore-GamingAI-Site/",
    code: "https://github.com/Vishvjeet-Rana/AetherCore-GamingAI-Site",
  },
  {
    id: "iphone",
    title: "3D iPhone Showcase",
    link: "https://vishvjeet-rana.github.io/iPhone15-Website-Three.js/",
    code: "https://github.com/Vishvjeet-Rana/iPhone15-Website-Three.js",
  },
  {
    id: "studysync",
    title: "StudySync",
    link: "https://vishvjeet-rana.github.io/Study-Sync-Responsive/",
    code: "https://github.com/Vishvjeet-Rana/Study-Sync-Responsive",
  },
  {
    id: "password",
    title: "Password Generator",
    link: "https://vishvjeet-rana.github.io/password-generator-reactjs/",
    code: "https://github.com/Vishvjeet-Rana/password-generator-reactjs",
  },
  {
    id: "spotify",
    title: "Spotify Landing",
    link: "https://vishvjeet-rana.github.io/Spotify-Landing-Page-Static-/",
    code: "https://github.com/Vishvjeet-Rana/Spotify-Landing-Page-Static-",
  },
  {
    id: "todo",
    title: "Todo List",
    link: "https://vishvjeet-rana.github.io/To-Do-List-JavaScript/",
    code: "https://github.com/Vishvjeet-Rana/To-Do-List-JavaScript",
  },
  {
    id: "ecom",
    title: "Ecommerce Site",
    link: "https://vishvjeet-rana.github.io/E-commerce-static-website/",
    code: "https://github.com/Vishvjeet-Rana/E-commerce-static-website",
  },
  {
    id: "simonsays",
    title: "Simon Says",
    link: "https://vishvjeet-rana.github.io/Simon-Says-Game/",
    code: "https://github.com/Vishvjeet-Rana/Simon-Says-Game",
  },
];
